// @flow
import _ from 'lodash';
import moment from 'moment';
import { NEW_ONBOARDING_SSR_USER } from 'src/constants/installation-v2';

/**
 * check enable onboarding v3
 * @param {*} tenantApp
 * @returns
 */
export const enableOnboardingV3 = tenantApp => {
  const enableOnboardingV3 = _.get(tenantApp, 'settings.onboardingV3.settings.enable', false);

  return enableOnboardingV3;
};

/**
 * Check show email input for store
 * @param {*} tenantApp
 * @returns
 */
export const checkUserShowEmailOpt = tenantApp => {
  const firstInstalledAt = _.get(tenantApp, 'firstInstalledAt');
  const optEmailAC = _.get(tenantApp, 'optEmail');
  const firstInstalled = moment(firstInstalledAt * 1000);
  const startNewOnboardingSSR = moment(NEW_ONBOARDING_SSR_USER);

  return !optEmailAC && firstInstalled.isAfter(startNewOnboardingSSR);
};

/**
 * getMinuteToCompleteText
 * it will return the minutes from the task is start to estimate time to finish
 * @param {*} startAt
 * @param {*} lastAt
 * @param {*} progress
 * @returns
 */
export const getMinuteToCompleteText = (startAt, lastAt, progress) => {
  // progress from 0 -> 1
  const estRangeToEnd = (lastAt - startAt) / progress;
  const estEndAt = startAt + estRangeToEnd;
  const diffFromStartToEstEnd = Math.abs(new Date(estEndAt * 1000) - new Date(startAt * 1000)) || 0;
  const minutesFromStartToEstEnd = Math.round(diffFromStartToEstEnd / 1000 / 60);

  const diffFromLastToNow = Math.abs(new Date() - new Date(lastAt * 1000)) || 0;
  const minutesFromLastToNow = Math.round(diffFromLastToNow / 1000 / 60);

  if (progress === 1) {
    return minutesFromStartToEstEnd;
  }
  return minutesFromStartToEstEnd + minutesFromLastToNow;
};

/**
 * Process the result from API to get percentage and step
 *
 * @param  {any}      result            respond data
 * @param  {number}   syncPercent       percent of previous sync
 * @param  {number}   setupThemePercent percent of previous theme setup
 *
 * @result {Object}   the name of current step, current step, total steps, percent of current sync and them setup
 */
export const processIntallStatus = (
  result: Object,
  syncPercent: number,
  setupThemePercent: number
) => {
  // Determine current install step
  const { currentStep, step, total } = getCurrentIntallStep(result);

  // Calculate percent for the progress
  const { progress } = _.get(result, 'syncStatus', {});
  const { status } = _.get(result, 'autoThemeSetupStatus', {});
  const percent = calculatePercent(progress, status, syncPercent, setupThemePercent);
  syncPercent = _.get(percent, 'syncPercent');
  setupThemePercent = _.get(percent, 'setupThemePercent');

  return { currentStep, step, total, syncPercent, setupThemePercent };
};

/**
 * Calculate the progress step of init
 *
 * @param  {any}    installStatus respond data
 *
 * @result {object} the name of current step, current step and total steps
 */
const getCurrentIntallStep = (installStatus: Object) => {
  const { progress = 0 } = _.get(installStatus, 'syncStatus', {});
  const progressFloat = parseFloat(progress) * 100;
  const { status } = _.get(installStatus, 'autoThemeSetupStatus', {});

  let total = 2;

  if (progressFloat < 100) {
    return {
      currentStep: 'AdminHTML.Installation.Helpers.getCurrentIntallStep_CurrentStep1',
      step: 1,
      total
    };
  }

  if (status && status === 'running') {
    return {
      currentStep: 'AdminHTML.Installation.Helpers.getCurrentIntallStep_CurrentStep2',
      step: 2,
      total
    };
  }

  return { currentStep: '', step: 0, total: 0 };
};

/**
 * Calculate the progress percentation of init
 *
 * @param  {any}    progress              progress of sync
 * @param  {number} recentSyncPercent       percent of previous sync
 * @param  {string} setupThemeStatus        status of previous sync
 * @param  {number} recentSetupThemePercent percent of previous setup theme
 *
 * @result {object} percent of current sync and setup theme
 */
const calculatePercent = (
  progress,
  setupThemeStatus,
  recentSyncPercent,
  recentSetupThemePercent
) => {
  const ratioMap = {
    syncProcess: 80,
    setupThemeProcess: 20
  };

  // If the sync progress is null, get recentSyncPercent
  let syncPercent = progress
    ? (parseFloat(progress) * 100 * ratioMap.syncProcess) / 100
    : recentSyncPercent;

  // Setup theme done => 20%
  if (setupThemeStatus === 'done') {
    return { syncPercent, setupThemePercent: 20 };
  }

  // Increase setup theme percent by adding the random value based on the rest percent need to be full filled
  if (setupThemeStatus === 'running') {
    const setupThemePercent =
      recentSetupThemePercent +
      (20 - recentSetupThemePercent) * Math.random() * Math.random() * 0.1;
    return { syncPercent, setupThemePercent };
  }

  // Setup theme error => recent setup theme percent
  if (setupThemeStatus === 'error') {
    return { syncPercent, setupThemePercent: recentSetupThemePercent };
  }

  // Only return sync percent if the setup theme percent cannot be determined
  return { syncPercent, setupThemePercent: 0 };
};
